import { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Center,
  Loader,
  Alert,
} from "@mantine/core";
import { IconFingerprint, IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { navigate } from "wouter/use-browser-location";
import { jsonGet, jsonPost, userLogout } from "../api";
import { useStore } from "../store";
import { arrayBufferToBase64Url, base64UrlDecode } from "./base64helpers";
import { GetUserAgentString } from "../UserAgentDetails";

// Full-screen gate shown when the tenant requires passkey sign-in and the signed-in user has none
// yet. The user must create a passkey (or sign out); the app shell renders this instead of any route
// while `mustEnrollPasskey` is set. Recovery from a lost passkey routes back through here too (an
// admin clears the old passkeys, the user signs in with their password and re-enrolls).
export default function ForcedPasskeyEnrollment() {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const clearMustEnrollPasskey = useStore((s) => s.clearMustEnrollPasskey);

  // Self-heal: if the user already has a passkey (enrolled on another device, or a stale flag),
  // drop the gate rather than trapping them on this screen.
  useEffect(() => {
    jsonGet("auth/passkey")
      .then((keys) => {
        if (Array.isArray(keys) && keys.length > 0) {
          clearMustEnrollPasskey();
          navigate("/", { replace: true });
        }
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const enroll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const options = await jsonPost("auth/passkey/register/begin");
      const publicKey = {
        challenge: base64UrlDecode(options.challenge),
        rp: { name: options.rp.name, id: options.rp.id },
        user: {
          id: base64UrlDecode(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        authenticatorSelection: options.authenticatorSelection,
        timeout: options.timeout,
        attestation: options.attestation,
        excludeCredentials: (options.excludeCredentials || []).map((c) => ({
          id: base64UrlDecode(c.id),
          type: c.type,
          transports: c.transports || ["internal"],
        })),
      };

      const credential = await navigator.credentials.create({ publicKey });
      // navigator.credentials.create resolves to null when the user cancels the prompt or the
      // browser can't create a passkey. Surface a retryable error rather than dereferencing null
      // (which would throw and leave the user stuck on this screen). The gate stays up on purpose —
      // they must enroll or sign out; the button is clickable again once `busy` resets below.
      if (!credential) {
        throw new Error(
          t("passkey_cancelled", "Passkey setup was cancelled or isn't available on this device."),
        );
      }

      await jsonPost("auth/passkey/register/complete", {
        deviceName: GetUserAgentString(),
        attestationResponse: {
          id: credential.id,
          rawId: arrayBufferToBase64Url(credential.rawId),
          response: {
            attestationObject: arrayBufferToBase64Url(credential.response.attestationObject),
            clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
            transports: credential.response.getTransports
              ? credential.response.getTransports()
              : [],
          },
          type: credential.type,
          clientExtensionResults: credential.getClientExtensionResults(),
        },
      });

      notifications.show({
        color: "teal",
        message: t("passkey_enrolled", "Passkey created — you're all set."),
      });
      clearMustEnrollPasskey();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Forced passkey enrollment failed:", error);
      notifications.show({
        color: "red",
        title: t("passkey_enroll_error", "Couldn't create passkey"),
        message: error.message,
      });
    } finally {
      setBusy(false);
    }
  };

  if (checking)
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );

  return (
    <Container size="xs" mt="xl">
      <Paper radius="md" p="xl" withBorder>
        <Stack>
          <Group gap="xs">
            <IconFingerprint size={24} />
            <Title order={3}>{t("passkey_required_title", "Set up a passkey")}</Title>
          </Group>
          <Alert variant="light" color="blue" icon={<IconInfoCircle />}>
            {t(
              "passkey_required_body",
              "Your organization requires passkey sign-in. Create a passkey now to continue — you'll use it to sign in from now on.",
            )}
          </Alert>
          <Button
            leftSection={<IconFingerprint size={16} />}
            onClick={enroll}
            loading={busy}
            radius="md"
          >
            {t("passkey_create", "Create passkey")}
          </Button>
          <Button variant="subtle" color="gray" onClick={() => userLogout()}>
            {t("sign_out", "Sign out")}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
